/**
 * Dados do dia para a aba Painel: uma consulta por tabela, todas filtradas
 * pelos pets com estadia hoje, em vez de uma consulta por card.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { formatStayRange, isBirthdayOnDate, type ScheduleReservation, type StayNature, type StayStatus } from "../schedule/scheduleModel";
import { normalizeStayTime, panelAlertCountByPetId, petSizeFromDisplayOrder, primaryOwnerPhone, type PanelPet } from "./panelModel";
import { resolveReservationPackIds } from "../reservation/reservationPacks";

export const panelDayKey = (clinicId: string | null, isoDate: string) => ["panel-day", clinicId, isoDate];
export const PANEL_DAY_STATUSES = ["scheduled", "checked_in", "checked_out"] as const;

interface StayDayRow {
  id: string;
  stay_date: string;
  status: string;
  check_in_time: string | null;
  check_out_time: string | null;
  period: string | null;
  pet_id: string;
  hotel_room_bed_id: string | null;
  stay_reservations: {
    id: string;
    module_type: string;
    stay_pack_id: string | null;
    stay_reservation_packs: Array<{ stay_pack_id: string; created_at: string }> | null;
    check_in_date: string;
    check_out_date: string | null;
    is_overnight: boolean;
    arrival_shift?: string | null;
    pets: {
      name: string;
      birth_date: string | null;
      size_category_id: string | null;
      size_category: { display_order: number } | null;
      owners: { id: string; name: string; owners_phones: Array<{ phone_number: string | null; is_primary: boolean | null }> | null } | null;
    } | null;
    stay_reservation_daycare_plan_usages: Array<{ id: string }> | null;
    stay_reservation_hotel_plan_usages: Array<{ id: string }> | null;
  } | null;
  hotel_room_beds: { name: string; hotel_rooms: { name: string } | null } | null;
}

/** `stay_reservation_days.status` usa "scheduled"; a UI do painel fala "reserved". */
const displayStatusOf = (status: string): StayStatus => (status === "scheduled" ? "reserved" : (status as StayStatus));

const bedLabelOf = (row: StayDayRow) => {
  const bed = row.hotel_room_beds;
  if (!bed) return null;
  return bed.hotel_rooms?.name ? `${bed.hotel_rooms.name} · ${bed.name}` : bed.name;
};

const natureOf = (moduleType: string): StayNature =>
  moduleType === "hotel" || moduleType === "both" ? moduleType : "daycare";

/**
 * Um `ScheduleReservation` completo, para reaproveitar `formatStayRange` com o
 * tipo real dele em vez de um cast — o mesmo padrão de `useStaySchedule.ts`.
 * O intervalo (`checkInDate`/`checkOutDate`) vem da reserva-mãe: é o que dá o
 * badge "17→21/06" mesmo consultando um único dia.
 */
const toScheduleReservation = (row: StayDayRow): ScheduleReservation => ({
  id: row.stay_reservations?.id ?? row.id,
  petId: row.pet_id,
  petName: row.stay_reservations?.pets?.name ?? "",
  ownerName: row.stay_reservations?.pets?.owners?.name ?? "",
  ownerId: row.stay_reservations?.pets?.owners?.id ?? null,
  birthDate: row.stay_reservations?.pets?.birth_date ?? null,
  nature: natureOf(row.stay_reservations?.module_type ?? "daycare"),
  checkInDate: row.stay_reservations?.check_in_date ?? row.stay_date,
  checkOutDate: row.stay_reservations?.check_out_date ?? null,
  checkInTime: row.check_in_time,
  status: displayStatusOf(row.status),
  isOvernight: row.stay_reservations?.is_overnight ?? false,
  bedLabel: bedLabelOf(row),
  stayPlan:
    (row.stay_reservations?.stay_reservation_daycare_plan_usages ?? []).length > 0 ||
    (row.stay_reservations?.stay_reservation_hotel_plan_usages ?? []).length > 0
      ? { covered: true, paid: false }
      : null,
});

/**
 * Os pets presentes ou esperados hoje, já com porte, alertas, adaptação e
 * incompatibilidades. Uma consulta por tabela, todas filtradas pelos pets do
 * dia, em vez de uma consulta por card.
 */
export function usePanelDay(isoDate: string) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: panelDayKey(clinicId, isoDate),
    enabled: !!clinicId && !!isoDate,
    queryFn: async (): Promise<PanelPet[]> => {
      if (!clinicId) return [];

      const { data: dayData, error: dayError } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .from("stay_reservation_days")
        .select(
          `id, stay_date, status, check_in_time, check_out_time, period, pet_id, hotel_room_bed_id,
           stay_reservations!inner (
             id, module_type, stay_pack_id, check_in_date, check_out_date, is_overnight, arrival_shift,
             stay_reservation_packs ( stay_pack_id, created_at ),
             pets ( name, birth_date, size_category_id, size_category:pet_size_categories ( display_order ), owners ( id, name, owners_phones ( phone_number, is_primary ) ) ),
             stay_reservation_daycare_plan_usages ( id ),
             stay_reservation_hotel_plan_usages ( id )
           ),
           hotel_room_beds ( name, hotel_rooms ( name ) )`,
        )
        .eq("clinic_id", clinicId)
        .eq("stay_date", isoDate)
        .in("status", PANEL_DAY_STATUSES);
      if (dayError) throw dayError;

      const rows = (dayData as unknown as StayDayRow[]) ?? [];
      if (rows.length === 0) return [];

      const petIds = [...new Set(rows.map((row) => row.pet_id))];

      // `pet_relationships` guarda cada par uma única vez, ordenado
      // canonicamente (pet_id < related_pet_id) — ver
      // pet_relationships_order_chk na migração 20260714154500. Um pet do
      // dia pode estar de qualquer lado do par, então o filtro precisa
      // cobrir os dois lados para todos os pets.
      const relationshipFilter = petIds.map((id) => `pet_id.eq.${id},related_pet_id.eq.${id}`).join(",");

      const [sizes, alerts, allergies, adaptations, relationships] = await Promise.all([
        supabase
          .from("pets")
          .select("id, size_category_id")
          .in("id", petIds),
        supabase
          .from("pet_custom_alerts")
          .select("pet_id, label, value")
          .eq("is_active", true)
          .in("pet_id", petIds),
        supabase
          .from("pet_conditions")
          .select("pet_id, conditions!inner(type)")
          .in("pet_id", petIds)
          .eq("conditions.type", "allergy"),
        supabase
          .from("stay_pet_adaptations")
          .select("pet_id")
          .eq("clinic_id", clinicId)
          .eq("is_active", true)
          .in("pet_id", petIds),
        supabase
          .from("pet_relationships")
          .select(
            `pet_id, related_pet_id, relationship,
             pet_side:pets!pet_relationships_pet_id_fkey ( name ),
             related_side:pets!pet_relationships_related_pet_id_fkey ( name )`,
          )
          // `relationship` é "likes" | "dislikes" | "neutral" — ver PetRelationshipKind
          // em ../reservation/petProfileModel.ts.
          .eq("relationship", "dislikes")
          .or(relationshipFilter),
      ]);
      if (sizes.error) throw sizes.error;
      if (alerts.error) throw alerts.error;
      if (allergies.error) throw allergies.error;
      if (adaptations.error) throw adaptations.error;
      if (relationships.error) throw relationships.error;

      const sizeCategoryIds = [...new Set(
        ((sizes.data ?? []) as Array<{ size_category_id: string | null }>)
          .map((row) => row.size_category_id)
          .filter((id): id is string => Boolean(id)),
      )];
      const { data: sizeCategories, error: sizeCategoriesError } = sizeCategoryIds.length > 0
        ? await supabase
            .from("pet_size_categories")
            .select("id, display_order")
            .in("id", sizeCategoryIds)
        : { data: [], error: null };
      if (sizeCategoriesError) throw sizeCategoriesError;

      const displayOrderByCategoryId = new Map(
        (sizeCategories ?? []).map((category) => [category.id, category.display_order]),
      );
      const sizeByPet = new Map<string, number | null>();
      for (const row of (sizes.data ?? []) as Array<{
        id: string;
        size_category_id: string | null;
      }>) {
        sizeByPet.set(
          row.id,
          row.size_category_id ? displayOrderByCategoryId.get(row.size_category_id) ?? null : null,
        );
      }

      const alertsByPet = new Map<string, string[]>();
      for (const row of (alerts.data ?? []) as Array<{ pet_id: string; label: string; value: string | null }>) {
        const text = row.value ? `${row.label}: ${row.value}` : row.label;
        alertsByPet.set(row.pet_id, [...(alertsByPet.get(row.pet_id) ?? []), text]);
      }
      const alertCountByPet = panelAlertCountByPetId(
        (alerts.data ?? []).map((row) => ({ petId: row.pet_id, isActive: true })),
        (allergies.data ?? []).map((row) => ({ petId: row.pet_id })),
      );

      const adaptingPets = new Set((adaptations.data ?? []).map((row) => row.pet_id));

      const petIdSet = new Set(petIds);
      const incompatibleByPet = new Map<string, string[]>();
      for (const row of (relationships.data ?? []) as Array<{
        pet_id: string;
        related_pet_id: string;
        pet_side: { name: string } | null;
        related_side: { name: string } | null;
      }>) {
        if (petIdSet.has(row.pet_id) && row.related_side?.name) {
          incompatibleByPet.set(row.pet_id, [...(incompatibleByPet.get(row.pet_id) ?? []), row.related_side.name]);
        }
        if (petIdSet.has(row.related_pet_id) && row.pet_side?.name) {
          incompatibleByPet.set(
            row.related_pet_id,
            [...(incompatibleByPet.get(row.related_pet_id) ?? []), row.pet_side.name],
          );
        }
      }

      return rows
        .map((row): PanelPet => ({
          reservationId: row.stay_reservations?.id ?? row.id,
          stayDayId: row.id,
          petId: row.pet_id,
          stayPackId: row.stay_reservations?.stay_pack_id ?? null,
          stayPackIds: resolveReservationPackIds(
            row.stay_reservations?.stay_reservation_packs ?? [],
            row.stay_reservations?.stay_pack_id ?? null,
          ),
          petName: row.stay_reservations?.pets?.name ?? "",
          ownerId: row.stay_reservations?.pets?.owners?.id ?? null,
          ownerName: row.stay_reservations?.pets?.owners?.name ?? "",
          nature: natureOf(row.stay_reservations?.module_type ?? "daycare"),
          status: displayStatusOf(row.status),
          size: petSizeFromDisplayOrder(sizeByPet.get(row.pet_id)),
          period: row.period,
          arrivalShift: row.stay_reservations?.arrival_shift === "morning" || row.stay_reservations?.arrival_shift === "evening"
            ? row.stay_reservations.arrival_shift
            : null,
          checkInTime: normalizeStayTime(row.check_in_time),
          checkOutTime: normalizeStayTime(row.check_out_time),
          ownerPhone: primaryOwnerPhone(row.stay_reservations?.pets?.owners?.owners_phones),
          isOvernight: row.stay_reservations?.is_overnight ?? false,
          inAdaptation: adaptingPets.has(row.pet_id),
          bedLabel: bedLabelOf(row),
          stayRange: formatStayRange(toScheduleReservation(row)),
          lastStayDate: row.stay_reservations?.check_out_date ?? row.stay_date,
          alerts: alertsByPet.get(row.pet_id) ?? [],
          alertCount: alertCountByPet.get(row.pet_id) ?? 0,
          incompatibleWith: incompatibleByPet.get(row.pet_id) ?? [],
          isBirthday: isBirthdayOnDate(row.stay_reservations?.pets?.birth_date ?? null, isoDate),
        }))
        .sort((left, right) => left.petName.localeCompare(right.petName));
    },
  });
}
